import Router from "next/router";

// For each history entry this tab navigated to inside Apollo, the entry it
// came from. Next keeps an entry's key on replace and mints a new one on
// push, so a replaced entry keeps pointing at what was behind it. The first
// entry (a cold load or a link from outside) has none.
const cameFrom = new Map<string, { key: string; url: string }>();
let leaving: { key: string; url: string } | null = null;
// The in-app page the current one was reached from, by any route change.
let previousUrl: string | null = null;

const currentKey = (): string | undefined =>
  typeof window === "undefined" ? undefined : window.history.state?.key;

if (typeof window !== "undefined") {
  Router.events.on("routeChangeStart", (url: string) => {
    const key = currentKey();
    leaving = key ? { key, url: Router.asPath } : null;
    // Recorded as the change starts: a cached page renders, and reads it,
    // before routeChangeComplete fires.
    if (url.split("?")[0] !== Router.asPath.split("?")[0]) {
      previousUrl = Router.asPath;
    }
  });
  Router.events.on("routeChangeComplete", () => {
    const key = currentKey();
    if (key && leaving && key !== leaving.key && !cameFrom.has(key)) {
      cameFrom.set(key, leaving);
    }
    leaving = null;
  });
}

/**
 * The Apollo pages behind the current entry, nearest first, for as far back
 * as this tab navigated inside Apollo.
 */
export const inAppTrail = (): string[] => {
  const trail: string[] = [];
  let key = currentKey() ?? "";
  while (cameFrom.has(key) && trail.length < 50) {
    const previous = cameFrom.get(key);
    trail.push(previous.url);
    key = previous.key;
  }
  return trail;
};

/**
 * The Apollo page this one was opened from, back or forward included. Read
 * once: a later remount of the same page (a view toggle) isn't an arrival.
 */
export const takeArrivedFrom = () => {
  const url = previousUrl;
  previousUrl = null;
  return url;
};
